import { requireNonNullable } from '@disy/lib/custom-error';
import type { DelegateEvent } from '@disy/lib/event-util';
import { on } from '@disy/lib/event-util';
import { clamp } from '@disy/lib/math';
import { isGreaterOrIsClose } from '@disy/lib/position';
import { px } from '@disy/lib/px';
import type { Teardown } from '@disy/lib/teardown';
import { throttle } from '@disy/lib/throttle';
import { uniqueId } from '@disy/lib/unique-id';

import { applyGlobalCursor } from 'cadenza/utils/global-cursor/global-cursor';
import 'cadenza/workbook/layout-management/resize-handle.css';
import type { WithResizeInfo } from 'cadenza/workbook/layout-management/resize-utils';
import { getResize } from 'cadenza/workbook/layout-management/resize-utils';
import {
  MARGIN,
  getBounds,
  getPosition,
  getViewPortWithoutScrollSize,
} from 'cadenza/workbook/selection-cart/layout/layout-utils';
import { handleSelectionCartMoving } from 'cadenza/workbook/selection-cart/layout/moving';
import {
  getSelectionCartResizeCursor,
  handleSelectionCartResizing,
} from 'cadenza/workbook/selection-cart/layout/resizing';
import type { SelectionCart } from 'cadenza/workbook/selection-cart/selection-cart';

const POSITION_OFFSET = 16;

export function setupSelectionCartLayout(selectionCart: WithResizeInfo<SelectionCart>) {
  const relativeContainer = requireNonNullable(selectionCart.parentElement);
  const teardowns: Teardown[] = [];
  let isSelectionCartDragOrResize = false;
  // should be uniqueId because there can be two selection-cart when it is shown in map-full-screen-dialog
  const globalCursorSourceId = uniqueId();
  const toggleResize = (event: PointerEvent) => {
    if (isSelectionCartDragOrResize || event.pointerType === 'touch') {
      return; // resizing or moving in progress; resizing for touch is currently not working
    }
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return; // pointer released outside the window, so there is no closest function
    }
    if (
      // condition to possibly show the resize handle for selection-cart
      !selectionCart.hidden &&
      !selectionCart.classList.contains('is-minimized') &&
      // there can be two selection-cart when it is shown in map-full-screen-dialog
      selectionCart === target.closest('.d-selection-cart')
    ) {
      selectionCart.dResize = getResize(event, selectionCart, MARGIN);
      applyGlobalCursor(getSelectionCartResizeCursor(selectionCart.dResize), globalCursorSourceId);
    } else {
      delete selectionCart.dResize;
      applyGlobalCursor('', globalCursorSourceId);
    }
  };
  teardowns.push(
    on(window, 'pointermove', throttle(toggleResize, 50, { trailing: false })),
    on(
      relativeContainer,
      'pointerdown',
      (event: DelegateEvent<PointerEvent>) => {
        // Only continue for mouse events if it's left click, and event.target is an Element
        if ((event.pointerType === 'mouse' && event.button !== 0) || !(event.target instanceof Element)) {
          return;
        }

        const isSelectionCartDragHandleClicked = event.target?.closest('.' + selectionCart.dragHandleClass);
        const selectionCartResizing = selectionCart.dResize && Object.values(selectionCart.dResize).some(Boolean);

        if (isSelectionCartDragHandleClicked) {
          isSelectionCartDragOrResize = true;
          handleSelectionCartMoving(event, selectionCart, relativeContainer, globalCursorSourceId);
        } else if (selectionCartResizing) {
          isSelectionCartDragOrResize = true;
          handleSelectionCartResizing(event, selectionCart, relativeContainer);
        }
      },
      { delegate: '.d-selection-cart' },
    ),

    // Add touchend because pointerup is not dispatched by Chrome.
    on(window, ['pointerup', 'touchend'], (event: PointerEvent | TouchEvent) => {
      isSelectionCartDragOrResize = false;
      // Resizing is currently not working on touch.
      if (event instanceof PointerEvent) {
        toggleResize(event);
      }
    }),
  );
  return teardowns;
}

export function position(x: number, y: number, selectionCart: SelectionCart) {
  const [viewPortWidth, viewPortHeight] = getViewPortWithoutScrollSize();
  const rect = selectionCart.getBoundingClientRect();
  const spaceRight = viewPortWidth - x - POSITION_OFFSET;
  const spaceBottom = viewPortHeight - y - POSITION_OFFSET;

  const isBottom = isGreaterOrIsClose(spaceBottom, rect.height);
  const isRight = isGreaterOrIsClose(spaceRight, rect.width);

  const style = selectionCart.style;
  if (isBottom) {
    style.top = px(y + POSITION_OFFSET);
  } else {
    style.top = px(Math.max(0, y - rect.height - POSITION_OFFSET));
  }
  if (isRight) {
    style.left = px(x + POSITION_OFFSET);
  } else {
    style.left = px(Math.max(0, x - rect.width - POSITION_OFFSET));
  }
}

/**
 * Adjust the position, so that the selection cart is within its bounds.
 */
export function adjustPositionWithinBounds(selectionCart: SelectionCart) {
  if (selectionCart.hidden) {
    return;
  }
  const selectionCartRect = selectionCart.getBoundingClientRect();
  const [viewPortWidth, viewPortHeight] = getViewPortWithoutScrollSize();
  const { maxBottom, maxLeft, maxRight, maxTop, minBottom, minLeft, minRight, minTop } = getBounds(
    selectionCartRect,
    viewPortWidth,
    viewPortHeight,
  );
  const relativePosition = getPosition(selectionCartRect, new DOMRect(0, 0, viewPortWidth, viewPortHeight));

  // Clamps the positions to min/max bounds.
  Object.assign(selectionCart.style, {
    top: px(clamp(relativePosition.top, minTop, maxTop)),
    right: px(clamp(relativePosition.right, minRight, maxRight)),
    bottom: px(clamp(relativePosition.bottom, minBottom, maxBottom)),
    left: px(clamp(relativePosition.left, minLeft, maxLeft)),
    // Set current width and height to not resize the cart when viewPort is changed
    // But, only when there is enough space available within the view-port.
    width: px(Math.min(selectionCartRect.width, viewPortWidth - 2 * MARGIN)),
    height: px(Math.min(selectionCartRect.height, viewPortHeight - 2 * MARGIN)),
  });
  selectionCart.storeLastPosition();
}
